import { useState, useEffect, useRef, ReactNode } from "react";
import { Editor } from "primereact/editor";
import { Toast } from "primereact/toast";
import REIDataTable from "../../../components/ui/dataTable";
import { REIButton, REIProgressSpinner, REIToastContainer } from "../../../components/ui";
import { httpPost, httpUpdate, httpDelete } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { Icons, MEMBER_KEY, Messages } from "../../../utils/constants";
import { confirmPopup } from "primereact/confirmpopup";

export interface IMemberPropertyComment {
  Comment: string;
  CommentId: string;
  DateCreated: string;
}

interface PropertyData {
  watchListId: string;
  MemberPropertyComment: IMemberPropertyComment[];
  RefreshData: () => void;
}

interface AddNote {
  Comment: string;
  CommentId: string;
  MemberId: string;
}

const EditorPortfolioTracker: React.FC<PropertyData> = ({ watchListId, MemberPropertyComment, RefreshData }) => {
  const [notes, setNotes] = useState<IMemberPropertyComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isedittorHidden, setIsedittorHidden] = useState(true);
  const [isEditButtonVisible, setIsEditButtonVisible] = useState(true);
  const [editorText, setEditorText] = useState<string>(null);
  const [refreshData, setRefreshData] = useState(false);
  const toastRef = useRef<Toast>(null);
  let addnote = useRef();
  let selectednote = useRef<AddNote>();
  const memberId = localStorage.getItem(MEMBER_KEY);

  useEffect(() => {
    setNotes([]);
    if (MemberPropertyComment.length > 0) {
      setNotes(MemberPropertyComment);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [watchListId, refreshData, MemberPropertyComment]);

  const handleEdit = (event: React.MouseEvent<HTMLElement>, row: AddNote) => {
    setEditorText(row.Comment);
    selectednote.current = row;
    setIsedittorHidden(false);
  };

  const handleDelete = (row: AddNote) => {
    setLoading(true);
    httpDelete(`${ApiEndPoints.tracker}Comments?memberId=${memberId}&CommentId=${row.CommentId}`, undefined)
      .then((result) => {
        if (result.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.DELETE_SUCCESS,
            detail: "Note Deleted Successfully",
            life: 2000,
          });
          RefreshData();
          setRefreshData(!refreshData);
        }
        setLoading(false);
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.DELETE_ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setLoading(false);
      });
  };

  const handleSave = async () => {
    if (selectednote.current != null) {
      handleEditNote(editorText);
    } else {
      handleAddNote(editorText);
    }
  };

  const handleAddNote = async (newText) => {
    addnote.current = {
      Comment: newText,
      MemberId: localStorage.getItem(MEMBER_KEY),
      MemberPropertyId: watchListId,
    };

    await httpPost(`${ApiEndPoints.tracker}Comments/`, addnote.current)
      .then((result) => {
        if (result.status == 200) {
          setIsedittorHidden(true);
          setEditorText(null);
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Note Added Successfully",
            life: 2000,
          });
          RefreshData();
          setRefreshData(!refreshData);
        }
      })
      .catch(() => {
        setEditorText(null);
        setIsedittorHidden(true);
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
      });
  };

  const handleEditNote = async (newText) => {
    if (selectednote.current.Comment === newText) {
      setIsedittorHidden(true);
      selectednote.current = undefined;
      return;
    }
    selectednote.current.Comment = newText;
    await httpUpdate(`${ApiEndPoints.tracker}Comments/`, {
      Comment: selectednote.current.Comment,
      CommentId: selectednote.current.CommentId,
      MemberId: localStorage.getItem(MEMBER_KEY),
    })
      .then((result) => {
        setIsedittorHidden(true);
        setEditorText("");
        if (result.status == 200) {
          selectednote.current = undefined;
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Note Updated Successfully",
            life: 2000,
          });
          RefreshData();
          setRefreshData(!refreshData);
        }
      })
      .catch(() => {
        selectednote.current = undefined;
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setLoading(false);
      });
  };

  const handleEditorToggle = () => {
    setIsedittorHidden(!isedittorHidden);
    selectednote.current = null;
    setEditorText(null);
  };

  const columns = [
    {
      header: "",
      field: "DateCreated",
      position: "center",
      className: "text-black-300 text-center",
    },
    {
      header: "",
      field: "Comment",
      position: "center",
      className: "w-34 text-ellipsis",
      bodyTemplate: (rowData: AddNote): ReactNode => <div dangerouslySetInnerHTML={{ __html: rowData.Comment }}></div>,
    },
    {
      field: "",
      type: "custom",
      onClick: handleEdit,

      template: (
        <i
          id="mpnote-icon-edit"
          data-testid="mpnote-icon-edit"
          aria-label="Edit Note"
          title="Edit Note"
          className="pi pi-pencil text-[#4E9FBA] cursor-pointer w-1/5 text-sm text-end"
        ></i>
      ),
    },
    {
      field: "",
      type: "custom",
      bodyTemplate: (rowData: AddNote) => (
        <i
          id={`mpnote-icon-delete-${rowData.CommentId}`}
          data-testid={`mpnote-icon-delete-${rowData.CommentId}`}
          aria-label="Delete Note"
          title="Delete Note"
          className={`pi pi-times text-[#4E9FBA] w-1/5 text-sm ${
            selectednote.current?.CommentId === rowData.CommentId ? "opacity-50 pointer-events-none" : "cursor-pointer"
          }`}
          onClick={(e) => {
            if (selectednote.current?.CommentId !== rowData.CommentId) {
              confirmDelete(e, rowData);
            }
          }}
        ></i>
      ),
    },
  ];
  const renderHeader = () => {
    return (
      <span className="ql-formats">
        <button id="mpnote-btn-bold" data-testid="mpnote-btn-bold" className="ql-bold" aria-label="Bold"></button>
        <button id="mpnote-btn-italic" data-testid="mpnote-btn-italic" className="ql-italic" aria-label="Italic"></button>
        <button id="mpnote-btn-underline" data-testid="mpnote-btn-underline" className="ql-underline" aria-label="Underline"></button>
      </span>
    );
  };

  const confirmDelete = (event: React.MouseEvent, row: AddNote) => {
    confirmPopup({
      target: event.currentTarget,
      message: Messages.CONFIRMATION_MESSAGE,
      icon: Icons.INFO_ICON,
      accept: () => handleDelete(row),
    });
  };

  return (
    <div>
      <REIToastContainer toastReference={toastRef} />
      {loading && <REIProgressSpinner />}
      {!loading && (
        <>
          <div className="max-h-36 overflow-auto">
            <REIDataTable
              data={notes}
              columns={columns}
              customclass="panel-box datagrid  border rounded-md mt-4 hide-datatable-header"
            />
          </div>
          <div className="m-3">
            <REIButton
              id="mpnote-btn-addnote"
              classNames="text-xs font-thin border-none p-1 borderRadius"
              onClick={handleEditorToggle}
              disabled={loading}
              label="+ Add note"
            ></REIButton>
          </div>
          <div className="card border border-slate-300" hidden={isedittorHidden}>
            <Editor
              headerTemplate={renderHeader()}
              value={editorText}
              onTextChange={(e) => setEditorText(e.htmlValue)}
            />
            <REIButton
              id="mpnote-btn-save"
              classNames="items-end text-xs p-1 m-1 "
              disabled={
                editorText == "" || editorText == null || editorText === selectednote.current?.Comment ? true : false
              }
              onClick={handleSave}
              label="Save"
            ></REIButton>
            <REIButton
              id="mpnote-btn-cancel"
              classNames="items-end text-xs p-1 m-1"
              disabled={loading}
              onClick={handleEditorToggle}
              label="Cancel"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EditorPortfolioTracker;
